import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { BackLinkComponent } from '../shared/components/back-link/back-link.component';
import { LoadingSpinnerComponent } from '../shared/components/loading-spinner/loading-spinner.component';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { combineLatest, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { JobService } from '../services/job.service';
import * as mapboxgl from 'mapbox-gl';
import { ResizableTextareaComponent } from '../shared/components/resizable-textarea/resizable-textarea.component';

interface LocationAnalysis {
  named_location: string | null;
  confirmed_feature: string | null;
  material: string | null;
  depth_category: string | null;
  confidence_score_0_10: number | null;
  pattern_insight: string | null;
}

interface CatchRow {
  catchId: string;
  fish_type: string;
  fish_type_full_name: string;
  fish_type_display_color: string;
  time: string;
  temperature: number | null;
  water_temperature: number | null;
  water_temperature_trend: string | null;
  tide_cycle: string | null;
  wind_speed: string | null;
  wind_direction: string | null;
  short_forecast: string | null;
  lat: number;
  lng: number;
  accuracy: number | null;
}

@Component({
  selector: 'app-catch-detail',
  templateUrl: './catch-detail.component.html',
  styleUrls: ['./catch-detail.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, DatePipe, DecimalPipe, MatProgressSpinnerModule, MatButtonModule, BackLinkComponent, LoadingSpinnerComponent, ResizableTextareaComponent],
})
export class CatchDetailComponent implements OnInit, OnDestroy {
  catch: CatchRow | null = null;
  isLoading = true;
  errorMessage: string | null = null;

  catchList: string[] = [];
  currentIndex = -1;

  locationAnalysis: LocationAnalysis | null = null;
  tacticalImageUrl: SafeUrl | null = null;
  contextImageUrl: SafeUrl | null = null;
  isAnalyzing = false;
  isGettingMaps = false;
  isDeleting = false;
  editingField: string | null = null;
  editValue: any = null;
  userNotes = '';
  isSavingNotes = false;
  lightboxUrl: SafeUrl | null = null;
  lightboxLabel = '';

  // Location map (persistent) + editing state
  isEditingLocation = false;
  isSavingLocation = false;
  private catchMap: mapboxgl.Map | null = null;
  private catchMarker: mapboxgl.Marker | null = null;

  private tacticalObjectUrl: string | null = null;
  private contextObjectUrl: string | null = null;
  private routeSub!: Subscription;
  private jobSub: Subscription | null = null;

  get prevId(): string | null {
    return this.currentIndex > 0 ? this.catchList[this.currentIndex - 1] : null;
  }
  get nextId(): string | null {
    return this.currentIndex < this.catchList.length - 1 ? this.catchList[this.currentIndex + 1] : null;
  }
  get listSize(): number {
    return this.catchList.length;
  }

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private jobService: JobService,
  ) {}

  ngOnInit(): void {
    this.routeSub = combineLatest([
      this.route.paramMap,
      this.route.queryParamMap
    ]).subscribe(([params, queryParams]) => {
      const catchId = params.get('id')!;
      const listParam = queryParams.get('list');

      this.catchList = listParam ? listParam.split(',') : [];
      this.currentIndex = this.catchList.indexOf(catchId);

      this.catch = null;
      this.isLoading = true;
      this.errorMessage = null;
      this.locationAnalysis = null;
      this.isAnalyzing = false;
      this.isGettingMaps = false;
      this.userNotes = '';
      this.isSavingNotes = false;
      this.jobSub?.unsubscribe();
      this.revokeImageUrls();
      this.destroyLocationMap();

      this.jobSub = this.jobService.getCompletions$('catch', Number(catchId)).subscribe(() => {
        this.loadAnalysis(catchId);
        this.loadMapImages(catchId);
      });

      this.loadCatch(catchId);
    });
  }

  private loadCatch(catchId: string): void {
    this.http.get<any>(`${environment.apiUrl}/catches/${catchId}`).subscribe({
      next: (feature) => {
        if (feature.error) {
          this.errorMessage = `Catch #${catchId} not found.`;
          this.isLoading = false;
          return;
        }

        const coords = feature.geometry?.type === 'Point'
          ? (feature.geometry as GeoJSON.Point).coordinates
          : [0, 0];
        const props = feature.properties ?? {};

        this.catch = {
          catchId,
          fish_type: props['fish_type'] ?? '',
          fish_type_full_name: props['fish_type_full_name'] ?? 'Unknown',
          fish_type_display_color: props['fish_type_display_color'] ?? '#ff0000',
          time: props['time'] ?? '',
          temperature: props['temperature'] ?? null,
          water_temperature: props['water_temperature'] ?? null,
          water_temperature_trend: props['water_temperature_trend'] ?? null,
          tide_cycle: props['tide_cycle'] ?? null,
          wind_speed: props['wind_speed'] ?? null,
          wind_direction: props['wind_direction'] ?? null,
          short_forecast: props['short_forecast'] ?? null,
          lat: coords[1],
          lng: coords[0],
          accuracy: props['accuracy'] ?? null,
        };
        this.userNotes = feature.user_notes ?? '';

        this.isLoading = false;
        this.cdr.detectChanges();
        this.initCatchMap();
        this.loadAnalysis(catchId);
        this.loadMapImages(catchId);
      },
      error: () => {
        this.errorMessage = 'Failed to load catch data.';
        this.isLoading = false;
      }
    });
  }

  triggerGetMaps(): void {
    if (!this.catch || this.isGettingMaps) return;
    const catchId = this.catch.catchId;
    this.isGettingMaps = true;
    this.cdr.detectChanges();

    this.http.post<any>(`${environment.apiUrl}/catches/${catchId}/analyze`, {}).subscribe({
      next: (res) => {
        if (res.job_id) this.jobService.register({ id: res.job_id, job_type: 'catch_map', entity_type: 'catch', entity_id: Number(catchId), status: 'queued', message: 'Generating catch map...', created_at: new Date().toISOString(), completed_at: null });
        this.isGettingMaps = false;
        this.cdr.detectChanges();
      },
      error: () => { this.isGettingMaps = false; this.cdr.detectChanges(); }
    });
  }

  triggerAnalysis(): void {
    if (!this.catch || this.isAnalyzing) return;
    const catchId = this.catch.catchId;
    this.isAnalyzing = true;
    this.cdr.detectChanges();

    this.http.post<any>(`${environment.apiUrl}/catches/${catchId}/analyze_location`, {}).subscribe({
      next: (res) => {
        if (res.job_id) this.jobService.register({ id: res.job_id, job_type: 'catch_analyze', entity_type: 'catch', entity_id: Number(catchId), status: 'queued', message: 'Analyzing catch location...', created_at: new Date().toISOString(), completed_at: null });
        this.isAnalyzing = false;
        this.cdr.detectChanges();
      },
      error: () => { this.isAnalyzing = false; this.cdr.detectChanges(); }
    });
  }

  private loadAnalysis(catchId: string): void {
    this.http.get<{ location_analysis: LocationAnalysis | null }>(
      `${environment.apiUrl}/catches/${catchId}/analysis`
    ).subscribe({
      next: (res) => {
        this.locationAnalysis = res.location_analysis;
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  private loadMapImages(catchId: string): void {
    for (const view of ['tactical', 'context'] as const) {
      this.http.get(`${environment.apiUrl}/catches/${catchId}/map/${view}`, {
        responseType: 'blob'
      }).subscribe({
        next: (blob) => {
          const objectUrl = URL.createObjectURL(blob);
          if (view === 'tactical') {
            if (this.tacticalObjectUrl) URL.revokeObjectURL(this.tacticalObjectUrl);
            this.tacticalObjectUrl = objectUrl;
            this.tacticalImageUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
          } else {
            if (this.contextObjectUrl) URL.revokeObjectURL(this.contextObjectUrl);
            this.contextObjectUrl = objectUrl;
            this.contextImageUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
          }
          this.cdr.detectChanges();
        },
        error: () => {}
      });
    }
  }

  private revokeImageUrls(): void {
    if (this.tacticalObjectUrl) { URL.revokeObjectURL(this.tacticalObjectUrl); this.tacticalObjectUrl = null; }
    if (this.contextObjectUrl) { URL.revokeObjectURL(this.contextObjectUrl); this.contextObjectUrl = null; }
    this.tacticalImageUrl = null;
    this.contextImageUrl = null;
  }

  openLightbox(url: SafeUrl, label: string): void {
    this.lightboxUrl = url;
    this.lightboxLabel = label;
  }

  closeLightbox(): void {
    this.lightboxUrl = null;
    this.lightboxLabel = '';
  }

  saveNotes(): void {
    if (!this.catch) return;
    this.isSavingNotes = true;
    this.cdr.detectChanges();
    this.http.patch(
      `${environment.apiUrl}/catches/${this.catch.catchId}/notes`,
      { user_notes: this.userNotes }
    ).subscribe({
      next: () => { this.isSavingNotes = false; this.cdr.detectChanges(); },
      error: () => { this.isSavingNotes = false; this.cdr.detectChanges(); }
    });
  }

  startEdit(field: string, currentValue: any): void {
    this.editingField = field;
    this.editValue = currentValue ?? '';
  }

  cancelEdit(): void {
    this.editingField = null;
    this.editValue = null;
  }

  saveEdit(field: string): void {
    if (!this.catch) return;
    const value = field === 'confidence_score_0_10' ? Number(this.editValue) : this.editValue;
    this.http.patch<{ location_analysis: LocationAnalysis }>(
      `${environment.apiUrl}/catches/${this.catch.catchId}/analysis`,
      { [field]: value }
    ).subscribe({
      next: (res) => {
        this.locationAnalysis = res.location_analysis;
        this.editingField = null;
        this.editValue = null;
        this.cdr.detectChanges();
      },
      error: () => { this.editingField = null; this.editValue = null; this.cdr.detectChanges(); }
    });
  }

  deleteCatch(): void {
    if (!this.catch || !confirm(`Delete Catch #${this.catch.catchId}? This cannot be undone.`)) return;
    this.isDeleting = true;
    this.cdr.detectChanges();
    this.http.delete(`${environment.apiUrl}/catches/${this.catch.catchId}`).subscribe({
      next: () => this.router.navigate(['/catches']),
      error: () => { this.isDeleting = false; this.cdr.detectChanges(); }
    });
  }

  navigateTo(id: string): void {
    this.router.navigate(['/catch', id], {
      queryParams: this.catchList.length ? { list: this.catchList.join(',') } : {}
    });
  }

  private buildAccuracyCircleGeoJSON(lat: number, lng: number, radiusM: number): GeoJSON.Feature {
    const R = 6378137;
    const pts = 64;
    const coords: [number, number][] = [];
    for (let i = 0; i <= pts; i++) {
      const angle = (i * 360 / pts) * Math.PI / 180;
      const dLat = (radiusM / R) * (180 / Math.PI);
      const dLng = (radiusM / (R * Math.cos(Math.PI * lat / 180))) * (180 / Math.PI);
      coords.push([lng + dLng * Math.cos(angle), lat + dLat * Math.sin(angle)]);
    }
    return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } };
  }

  private initCatchMap(): void {
    if (!this.catch) return;
    // Destroy any previous map instance (navigating between catches)
    this.catchMarker?.remove();
    this.catchMarker = null;
    this.catchMap?.remove();
    this.catchMap = null;
    this.isEditingLocation = false;

    setTimeout(() => {
      if (!this.catch) return;
      this.catchMap = new mapboxgl.Map({
        accessToken: environment.mapbox.accessToken,
        container: 'catch-location-map',
        style: 'mapbox://styles/mapbox/standard-satellite',
        center: [this.catch.lng, this.catch.lat],
        zoom: 16,
        dragRotate: false,
        touchPitch: false
      });
      this.catchMarker = new mapboxgl.Marker({ color: '#ff4444', draggable: false })
        .setLngLat([this.catch.lng, this.catch.lat])
        .addTo(this.catchMap);

      if (this.catch.accuracy && this.catch.accuracy > 0) {
        this.catchMap.on('load', () => {
          const circle = this.buildAccuracyCircleGeoJSON(this.catch!.lat, this.catch!.lng, this.catch!.accuracy!);
          this.catchMap!.addSource('accuracy-circle', { type: 'geojson', data: circle as any });
          this.catchMap!.addLayer({
            id: 'accuracy-fill',
            type: 'fill',
            source: 'accuracy-circle',
            paint: { 'fill-color': '#4488ff', 'fill-opacity': 0.15 },
          });
          this.catchMap!.addLayer({
            id: 'accuracy-outline',
            type: 'line',
            source: 'accuracy-circle',
            paint: { 'line-color': '#4488ff', 'line-width': 1.5, 'line-dasharray': [3, 3] },
          });
        });
      }
    }, 0);
  }

  startEditLocation(): void {
    if (!this.catch || !this.catchMarker) return;
    this.isEditingLocation = true;
    this.catchMarker.setDraggable(true);
    this.cdr.detectChanges();
  }

  saveLocation(): void {
    if (!this.catch || !this.catchMarker) return;
    this.isSavingLocation = true;
    this.cdr.detectChanges();
    const { lat, lng } = this.catchMarker.getLngLat();
    this.http.patch<{ id: number; lat: number; long: number }>(
      `${environment.apiUrl}/catches/${this.catch.catchId}/location`,
      { lat, long: lng }
    ).subscribe({
      next: (res) => {
        this.catch!.lat = res.lat;
        this.catch!.lng = res.long;
        this.catchMarker!.setDraggable(false);
        this.isEditingLocation = false;
        this.isSavingLocation = false;
        // Update accuracy circle center
        if (this.catchMap?.getSource('accuracy-circle')) {
          const circle = this.buildAccuracyCircleGeoJSON(res.lat, res.long, this.catch!.accuracy!);
          (this.catchMap.getSource('accuracy-circle') as mapboxgl.GeoJSONSource).setData(circle as any);
        }
        this.cdr.detectChanges();
      },
      error: () => { this.isSavingLocation = false; this.cdr.detectChanges(); }
    });
  }

  cancelEditLocation(): void {
    if (!this.catch || !this.catchMarker) return;
    this.catchMarker.setLngLat([this.catch.lng, this.catch.lat]);
    this.catchMarker.setDraggable(false);
    this.isEditingLocation = false;
    this.cdr.detectChanges();
  }

  private destroyLocationMap(): void {
    this.catchMarker?.remove();
    this.catchMarker = null;
    this.catchMap?.remove();
    this.catchMap = null;
    this.isEditingLocation = false;
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.jobSub?.unsubscribe();
    this.revokeImageUrls();
    this.destroyLocationMap();
  }
}
